import React from "react";
import { View, StyleSheet, Pressable, ScrollView } from "react-native";
import { AlertCircle } from "lucide-react";
import Logo from "../../general/logos/Logo";
import CustomTextInput from "../../common/general/CustomTextInput/CustomTextInput";
import AxioButton from "../../axio-ds/Button/AxioButton";
import CustomText from "../../common/general/CustomText/CustomText";
import { DatePickerInput } from "react-native-paper-dates";
import { Checked, UnChecked } from "../../svgs/common";
import { colors, spacing, radii } from "../../../styles/design-system";
import { useAppConfig } from "../../../AppConfigProvider";

interface SignupFormProps {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  birthday: Date | null;
  dailyEmailOptIn: boolean;
  hasAgreedToTerms: boolean;
  nameError?: string;
  emailError?: string;
  passwordError?: string;
  confirmPasswordError?: string;
  birthdayError?: string;
  termsError?: string;
  logo: string;
  errorMessage: string;
  isLoading: boolean;
  onTermsPress: () => void;
  onSubmit: () => void;
  onSignInPress: () => void;
  onNameChange: (text: string) => void;
  onEmailChange: (text: string) => void;
  onPasswordChange: (text: string) => void;
  onConfirmPasswordChange: (text: string) => void;
  onBirthdayChange: (date: Date | null) => void;
  onNameBlur: () => void;
  onEmailBlur: () => void;
  onPasswordBlur: () => void;
  onConfirmPasswordBlur: () => void;
  onDailyEmailToggle: () => void;
  onTermsToggle: () => void;
  passwordHidden: boolean;
  confirmPasswordHidden: boolean;
  onPasswordToggle: () => void;
  onConfirmPasswordToggle: () => void;
  isValid: boolean;
}

function SignupForm({
  name,
  email,
  password,
  confirmPassword,
  birthday,
  dailyEmailOptIn,
  hasAgreedToTerms,
  nameError,
  emailError,
  passwordError,
  confirmPasswordError,
  birthdayError,
  termsError,
  logo,
  errorMessage,
  isLoading,
  onTermsPress,
  onSubmit,
  onSignInPress,
  onNameChange,
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onBirthdayChange,
  onNameBlur,
  onEmailBlur,
  onPasswordBlur,
  onConfirmPasswordBlur,
  onDailyEmailToggle,
  onTermsToggle,
  passwordHidden,
  confirmPasswordHidden,
  onPasswordToggle,
  onConfirmPasswordToggle,
  isValid,
}: SignupFormProps) {
  const { primaryColor } = useAppConfig();

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled">
      {/* Logo & Header */}
      <View style={styles.logoWrapper}>
        <Logo uri={logo} />
      </View>

      <CustomText
        text="Create your free account"
        style={styles.heading}
        role="heading"
        accessibilityRole="header"
        aria-level={1}
      />

      {/* Error Alert */}
      {errorMessage ? (
        <View style={styles.errorAlert}>
          <View style={styles.errorAlertContent}>
            <AlertCircle size={20} color={colors.error[600]} style={styles.alertIcon} aria-hidden="true" />
            <View style={styles.errorAlertContentFlex}>
              <CustomText text="Submission Error" style={styles.errorAlertTitle} />
              <CustomText text={errorMessage} style={styles.errorAlertText} />
            </View>
          </View>
        </View>
      ) : null}

      {/* Form Content */}
      <View style={styles.form}>
        {/* First Name Field */}
        <View style={styles.formGroup}>
          <CustomText text="First Name" style={styles.label} />
          <CustomTextInput
            value={name}
            onChangeText={onNameChange}
            onBlur={onNameBlur}
            placeholder="First Name..."
            autoComplete="given-name"
            style={[styles.input, nameError && styles.inputError]}
            aria-label="First name text input"
            accessibilityHint="Enter your first name"
            error={nameError}
            useTranslationText={true}
          />
        </View>

        {/* Email Field */}
        <View style={styles.formGroup}>
          <CustomText text="Email Address" style={styles.label} />
          <CustomTextInput
            value={email}
            onChangeText={onEmailChange}
            onBlur={onEmailBlur}
            placeholder="Email..."
            autoComplete="email"
            style={[styles.input, emailError && styles.inputError]}
            aria-label="Email text input"
            accessibilityHint="Enter your email address"
            error={emailError}
            useTranslationText={true}
          />
        </View>

        {/* Password Field */}
        <View style={styles.formGroup}>
          <CustomText text="Password" style={styles.label} />
          <CustomTextInput
            value={password}
            placeholder="Password..."
            style={[styles.input, passwordError && styles.inputError]}
            onChangeText={onPasswordChange}
            onBlur={onPasswordBlur}
            autoComplete="new-password"
            secureTextEntry={passwordHidden}
            onToggleSecureEntry={onPasswordToggle}
            aria-label="Password text input"
            accessibilityHint="Create a password"
            error={passwordError}
            useTranslationText={true}
          />
        </View>

        {/* Confirm Password Field */}
        <View style={styles.formGroup}>
          <CustomText text="Confirm Password" style={styles.label} />
          <CustomTextInput
            value={confirmPassword}
            placeholder="Confirm Password..."
            style={[styles.input, confirmPasswordError && styles.inputError]}
            onChangeText={onConfirmPasswordChange}
            onBlur={onConfirmPasswordBlur}
            autoComplete="new-password"
            secureTextEntry={confirmPasswordHidden}
            onToggleSecureEntry={onConfirmPasswordToggle}
            aria-label="Confirm password text input"
            accessibilityHint="Re-enter your password"
            error={confirmPasswordError}
            useTranslationText={true}
          />
        </View>

        {/* Birthday Field */}
        <View style={styles.formGroup}>
          <CustomText text="Birthday" style={styles.label} />
          <View style={[styles.datePickerContainer, birthdayError && styles.inputError]}>
            <DatePickerInput
              locale="en"
              value={birthday}
              onChange={onBirthdayChange}
              inputMode="start"
              mode="outlined"
              autoComplete="bday"
              activeOutlineColor={primaryColor}
              outlineColor={birthdayError ? colors.error[600] : colors.grey[200]}
              theme={{
                colors: {
                  primary: primaryColor,
                  onSurface: colors.grey[900],
                  error: colors.error[600],
                },
                roundness: radii.md,
              }}
              style={styles.datePicker}
              aria-label="Birthday date picker"
              accessibilityHint="Select your date of birth"
            />
            {birthdayError && <CustomText text={birthdayError} style={styles.fieldError} />}
          </View>
        </View>

        {/* Checkboxes */}
        <View style={styles.checkboxesContainer}>
          <Pressable
            style={styles.checkboxRow}
            onPress={onDailyEmailToggle}
            accessibilityRole="checkbox"
            aria-checked={dailyEmailOptIn}
            accessibilityLabel="Receive daily plan email">
            <View style={styles.checkboxIcon}>{dailyEmailOptIn ? <Checked /> : <UnChecked />}</View>
            <CustomText text="Receive daily plan email" style={styles.checkboxText} />
          </Pressable>

          <View>
            <Pressable
              style={styles.checkboxRow}
              onPress={onTermsToggle}
              accessibilityRole="checkbox"
              aria-checked={hasAgreedToTerms}
              accessibilityLabel="Agree to Terms of Service and Privacy Policy">
              <View style={styles.checkboxIcon}>{hasAgreedToTerms ? <Checked /> : <UnChecked />}</View>
              <View style={styles.termsTextContainer}>
                <CustomText text="I agree to the" style={[styles.checkboxText, { marginRight: spacing.xxs }]} />
                <Pressable
                  onPress={onTermsPress}
                  accessibilityRole="link"
                  role="link"
                  accessibilityLabel="Terms of Service and Privacy Policy">
                  <CustomText
                    text="Terms of Service and Privacy Policy"
                    style={[styles.link, { color: primaryColor }]}
                    aria-hidden={true}
                  />
                </Pressable>
              </View>
            </Pressable>
            {termsError && <CustomText text={termsError} style={styles.fieldError} />}
          </View>
        </View>

        {/* Signup Button */}
        <View style={styles.buttonWrapper}>
          <AxioButton
            label="Sign up"
            accessibilityLabel="Press to sign up"
            size="lg"
            variant="primary"
            style={styles.signupButton}
            onPress={onSubmit}
            disabled={isLoading || !isValid}
            isLoading={isLoading}
            useTranslationText={true}
          />
        </View>
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <View style={styles.footerRow}>
          <CustomText text="Already a member?" style={styles.footerText} />
          <Pressable onPress={onSignInPress} accessibilityRole="link">
            <CustomText text="Sign in" style={[styles.link, { color: primaryColor }]} />
          </Pressable>
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  contentContainer: {
    flexGrow: 1,
    paddingVertical: spacing.xl,
    paddingHorizontal: spacing.md,
    width: "100%",
    maxWidth: 600,
    alignSelf: "center",
  },
  scrollView: {
    flex: 1,
    width: "100%",
    backgroundColor: colors.white,
  },
  logoWrapper: {
    marginBottom: spacing.xl,
  },
  heading: {
    fontSize: 24,
    fontWeight: "600",
    color: colors.grey[900],
    marginBottom: spacing.xl,
  },
  errorAlert: {
    backgroundColor: colors.error[50],
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.error[200],
    padding: spacing.sm,
    marginBottom: spacing.lg,
  },
  errorAlertContent: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  alertIcon: {
    marginRight: spacing.xs,
    marginTop: 2,
  },
  errorAlertContentFlex: {
    flex: 1,
  },
  errorAlertTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: colors.error[800],
    marginBottom: spacing.xxs,
  },
  errorAlertText: {
    fontSize: 14,
    color: colors.error[700],
  },
  form: {
    width: "100%",
  },
  formGroup: {
    marginBottom: spacing.md,
  },
  label: {
    fontSize: 14,
    fontWeight: "500",
    color: colors.grey[900],
    marginBottom: 6,
  },
  input: {
    backgroundColor: colors.white,
    borderColor: colors.grey[200],
  },
  inputError: {
    borderColor: colors.error[600],
  },
  datePickerContainer: {
    backgroundColor: colors.white,
    borderRadius: radii.md,
    overflow: "hidden",
  },
  datePicker: {
    backgroundColor: colors.white,
    height: 48,
  },
  fieldError: {
    fontSize: 12,
    color: colors.error[600],
    marginTop: 4,
    marginLeft: spacing.xs,
  },
  checkboxesContainer: {
    marginTop: spacing.sm,
    gap: spacing.sm,
  },
  checkboxRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  checkboxIcon: {
    marginRight: spacing.sm,
  },
  checkboxText: {
    fontSize: 14,
    color: colors.grey[700],
  },
  termsTextContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
  },
  link: {
    fontSize: 14,
    fontWeight: "600",
  },
  buttonWrapper: {
    marginTop: spacing.xl,
  },
  signupButton: {
    width: "100%",
    height: 60,
    borderRadius: radii.md,
  },
  footer: {
    marginTop: spacing.xl,
    alignItems: "center",
  },
  footerRow: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  footerText: {
    fontSize: 14,
    color: colors.grey[600],
    marginRight: spacing.xxs,
  },
});

export default React.memo(SignupForm);
